import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAccessKey } from "./baseQuery";

export const marketApi = createApi({
  reducerPath: "marketApi",
  baseQuery: baseQueryWithAccessKey,
  tagTypes: ["MarketProduct"],
  endpoints: (builder) => ({
    getMarketProducts: builder.query({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        search_query = "",
        filters = [],
        sort_by = "",
      } = {}) => {
        // Clean the search_query parameter to handle special characters properly
        const cleanedSearchQuery = search_query
          ? search_query
              .replace(/\+/g, ' ') // Replace + with space
              .replace(/\|/g, '') // Remove | characters
              .replace(/[%]/g, '') // Remove % characters that can cause URL encoding issues
              .replace(/[^\w\s.-]/g, '') // Remove special characters except alphanumeric, spaces, dots, and hyphens
              .replace(/\s+/g, ' ') // Replace multiple spaces with single space
              .trim() // Remove leading/trailing whitespace
          : "";

        const params = {
          page: "MarketProduct_All",
          page_number: pageNumber,
          page_size: pageSize,
          ...(cleanedSearchQuery && { search_query: cleanedSearchQuery }),
          ...(filters.length > 0 && { filters: JSON.stringify(filters) }),
          ...(sort_by && { sort_by }),
        }

        // Log the final payload being sent to backend
        console.log("🚀 MARKET PRODUCTS PAYLOAD TO BACKEND:", {
          url: "/api/glabp/",
          params: params,
          filters_parsed: filters.length > 0 ? filters : "No filters",
          search_term: cleanedSearchQuery || "No search",
          sort_by: sort_by || "No sorting",
        });

        // Build custom URL to avoid + encoding for spaces
        const baseUrl = "/api/glabp/";
        const queryParams = new URLSearchParams();
        
        // Add each parameter manually
        queryParams.append("page", "MarketProduct_All");
        queryParams.append("page_number", pageNumber);
        queryParams.append("page_size", pageSize);
        
        if (cleanedSearchQuery) {
          queryParams.append("search_query", cleanedSearchQuery);
        }
        
        if (filters.length > 0) {
          queryParams.append("filters", JSON.stringify(filters));
        }
        
        if (sort_by) {
          queryParams.append("sort_by", sort_by);
        }

        // Convert to string and replace + with %20 for spaces
        const queryString = queryParams.toString().replace(/\+/g, '%20');
        const finalUrl = `${baseUrl}?${queryString}`;

        return {
          url: finalUrl,
          timeout: 120000, // 2 minutes timeout for large data
        };
      },
      providesTags: (
        result,
        error,
        { pageNumber, search_query, filters, sort_by }
      ) => [
        {
          type: "MarketProduct",
          id: `page-${pageNumber}-search-${search_query}-filters-${JSON.stringify(
            filters
          )}-sort-${sort_by}`,
        },
      ],
    }),

    getMarketProductDetails: builder.query({
      query: ({
        glid,
        pageNumber = 1,
        pageSize = 10,
        search_query = "",
        filters = [],
        sort_by = "",
      } = {}) => {
        // Clean the search_query parameter to handle special characters properly
        const cleanedSearchQuery = search_query
          ? search_query
              .replace(/\+/g, ' ') // Replace + with space
              .replace(/\|/g, '') // Remove | characters
              .replace(/[%]/g, '') // Remove % characters that can cause URL encoding issues
              .replace(/[^\w\s.-]/g, '') // Remove special characters except alphanumeric, spaces, dots, and hyphens
              .replace(/\s+/g, ' ') // Replace multiple spaces with single space
              .trim() // Remove leading/trailing whitespace
          : "";

        // Build custom URL to avoid + encoding for spaces
        const baseUrl = "/api/glabp/";
        const queryParams = new URLSearchParams();
        
        // Add each parameter manually
        queryParams.append("page", "MarketProduct_Details");
        queryParams.append("page_number", pageNumber);
        queryParams.append("page_size", pageSize);
        queryParams.append("glid", glid);
        
        if (cleanedSearchQuery) {
          queryParams.append("search_query", cleanedSearchQuery);
        }
        
        if (filters.length > 0) {
          queryParams.append("filters", JSON.stringify(filters));
        }
        
        if (sort_by) {
          queryParams.append("sort_by", sort_by);
        }

        // Convert to string and replace + with %20 for spaces
        const queryString = queryParams.toString().replace(/\+/g, '%20');
        const finalUrl = `${baseUrl}?${queryString}`;

        // Log the final payload being sent to backend
        console.log("🚀 FINAL PAYLOAD TO BACKEND:", {
          url: finalUrl,
          filters_parsed: filters.length > 0 ? filters : "No filters",
          search_term: cleanedSearchQuery || "No search",
          sort_by: sort_by || "No sorting",
        });

        return {
          url: finalUrl,
          timeout: 120000, // 2 minutes timeout
        };
      },
      providesTags: (
        result,
        error,
        { glid, pageNumber, search_query, filters, sort_by }
      ) => [
        {
          type: "MarketProduct",
          id: `details-${glid}-page-${pageNumber}-search-${search_query}-filters-${JSON.stringify(
            filters
          )}-sort-${sort_by}`,
        },
      ],
    }),
  }),
});

export const { useGetMarketProductsQuery, useGetMarketProductDetailsQuery } =
  marketApi;
